<template>
	<div class="laser-form">
		<div class="laser-form__content">
			<div class="laser-form__header">
				<!-- DEV: hardcoded title for testing, This title should be dynamic depending on the users selection from the menu button. The title options are:
			New laser, Edit laser, and Copy laser -->
				<div class="laser-form__header-primary">
					<span class="laser-form__header-title">
						{{ titleData }}
					</span>

					<!-- DEV: the autosave toggle should be enabled by default and will save the document every 5 minutes.  -->
					<span class="laser-from__header-autosave">
						<Toggle
							:label="'Autosave'"
							:disabled="!editionMode"
							:value="autoSave"
							@change="changeAutoSave"
							ref="tooltipEditModeRef"
							@mouseenter.native="tooltipAutosaveVisible = true"
							@focus.native="tooltipAutosaveVisible = true"
							@mouseleave.native="tooltipAutosaveVisible = false"
							@blur.native="tooltipAutosaveVisible = false"
						>
							<template slot="true-value">on</template>
							<template slot="false-value">off</template>
						</Toggle>
						<Tooltip
							:variation="variation"
							:text="editionMode ? status : 'Not allowed'"
							:trackedRefName="'tooltipEditModeRef'"
							:display="tooltipAutosaveVisible"
							:offset="[4, 8]"
							position="top-start"
						/>
					</span>
				</div>
				<span class="laser-form__header-spacer" />
				<div class="laser-form__header-secondary">
					<Toggle
						class="laser-form__header-secondary-toggle"
						:label="
							editionMode
								? form.active
									? 'Active'
									: 'Inactive'
								: 'Active'
						"
						:disabled="!editionMode"
						:value="form.active"
						@change="changeActiveDeactivate"
					/>
					<span class="laser-form__header-spacer" />
					<Button
						:className="'btn--icon-only-circle'"
						labelText=""
						:btnClick="showFormsHistory"
						iconClassName="icon-history"
						:iconBeforeLabel="true"
						:disabled="!editionMode"
					/>
				</div>
			</div>
			<hr class="laser-form__header-horizontal-line" />
			<div class="laser-form__group">
				<div class="laser-form__group-primary">
					<div class="laser-form__group-item">
						<Input
							className="field__input"
							label="Form Name"
							:maxLength="20"
							:required="true"
							v-model="form.name"
							:forceCustomErrorMessage="
								formNameCustomErrorMessage
							"
							@input="setName"
						/>
					</div>

					<div class="laser-form__group-item">
						<Input
							className="field__input"
							label="Form Revision"
							:maxLength="100"
							v-model="form.formRevision"
							:forceCustomErrorMessage="
								formRevisionCustomErrorMessage
							"
							@input="setFormRevision"
						/>
					</div>
				</div>

				<div class="laser-form__group-secondary">
					<div class="laser-form__group-item">
						<Textarea
							label="Form Description"
							:className="'textarea__input'"
							:cols="30"
							:maxLength="100"
							:required="true"
							:rows="5"
							:showAlwaysCharacterCounter="true"
							v-model="form.description"
							:forceCustomErrorMessage="
								formDescriptionCustomErrorMessage
							"
							@input="setDescription"
						/>
					</div>
				</div>

				<div class="laser-form__group-tertiary">
					<div class="laser-form-group__item">
						<!-- DEV NOTES: The uploaded file container will show when the form has already been created and is being edited.-->
						<div
							:class="[
								'uploaded-file',
								fileUploaded && 'is-file-uploaded'
							]"
						>
							<span
								class="uploaded-file__title"
								v-if="fileUploaded"
							>
								Selected File
							</span>
							<div
								class="uploaded-file__already-uploaded"
								v-if="fileUploaded"
							>
								<div class="uploaded-file__container">
									<span class="uploaded-file__icon">
										<i class="icon icon-document"></i>
									</span>

									<a
										@click="getFile"
										class="link uploaded-file__name"
									>
										{{ fileNameUploaded }}</a
									>
								</div>
								<div class="uploaded-file__delete">
									<Button
										:iconBeforeLabel="true"
										:className="'btn--icon-only'"
										:iconClassName="'icon-delete-glenn'"
										:title="changeRemoveTitleButton"
										:btnClick="deleteFile"
										:disabledInfo="!showDeleteFileOption"
									/>
								</div>
							</div>
							<!-- DEV NOTES: They will not be required to delete the file first. If a file has already been uploaded they need to see an "Upload File" option.
							Only one file can be uploaded at a time so new uploads will replace the old. PDF is not required to save a form so they should still be able to delete a file that has been uploaded and have no file attached.   -->
							<div class="uploaded-file__upload-or-replace-btn">
								<Button
									className="btn--has-icon"
									:labelText="
										fileUploaded
											? 'Replace File'
											: 'Upload File'
									"
									:iconBeforeLabel="true"
									iconClassName="icon-upload"
									:title="
										fileUploaded
											? 'Replace File'
											: 'Upload File'
									"
									:btnClick="addReplaceFile"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- DEV: Laser forms require 3 additional fields to be defined on the details of the form. These fields are only required when the form is a Reynolds form -->
			<div class="laser-form__optional">
				<div class="laser-form__check">
					<Checkbox
						labelText="Use PDF font"
						:displayLabel="true"
						@input="changeUsePDFFont"
						:value="true"
						:modelValue="usePDFFont"
					/>
					<Checkbox
						labelText="Include Form Charges"
						:displayLabel="true"
						@input="changeCharges"
						:value="true"
						:modelValue="showFormCharges"
					/>
				</div>

				<div class="laser-form__optional-fields" v-if="showFormCharges">
					<hr class="laser-form__optional-horizontal-line" />
					<div class="laser-form__optional-fields-container">
						<div class="laser-form__group-item">
							<Dropdown
								label="Type"
								:displayHelp="false"
								:options="typeOptions"
								v-model="form.formType"
							/>
						</div>
						<div class="laser-form__group-item">
							<Input
								className="field__input"
								label="Price"
								placeholder="99.9999"
								v-model="form.formPrice"
								:inputType="'Number'"
								:disabled="enabledPriceCost"
							/>
						</div>
						<div class="laser-form__group-item">
							<Input
								className="field__input"
								label="Cost"
								placeholder="99.9999"
								v-model="form.formCost"
								:inputType="'Number'"
								:disabled="enabledPriceCost"
							/>
						</div>
					</div>
				</div>
			</div>
			<div class="laser-form__details">
				<LaserFormContent
					v-model="form"
					v-if="hasFormValue"
					:editionMode="editionMode"
				/>
			</div>
		</div>

		<FileUploadModal
			v-model="showFileModal"
			:title="fileUploaded ? 'Replace' : 'Upload'"
			:formId="form.id"
			:acceptedFileType="'pdf'"
			:maximunSize="250"
			@change="changedFile"
		/>
	</div>
</template>

<script>
import Button from '@/components/forms/button/button';
import Toggle from '@/components/forms/selection-control/toggle/toggle';
import Input from '@/components/forms/input/input';
import Textarea from '@/components/forms/textarea/textarea.vue';
import Dropdown from '@/components/forms/dropdown/dropdown.vue';
import Checkbox from '@/components/forms/selection-control/checkbox/checkbox';
import LaserFormContent from '@/views/form-designer/laser-form-content.vue';
import CookiesConfig from '@/config/cookies';
import FileUploadModal from '@/site-components/file-upload/file-upload';
import LaserImpactForm from '@/mixins/forms/laser-impact-form';
import Tooltip from '@/components/shells/tooltip/tooltip';
import PdfFnForm from '@/mixins/forms/pdf-fn-form';
import FormShared from '@/mixins/forms/form-shared';
import { isEmpty } from '@/utils/object';
import { required, maxLength } from 'vuelidate/lib/validators';

export default {
	name: 'LaserForm',
	mixins: [LaserImpactForm, PdfFnForm, FormShared],
	model: {
		prop: 'form'
	},
	components: {
		Button,
		Toggle,
		Input,
		Textarea,
		Checkbox,
		Dropdown,
		LaserFormContent,
		FileUploadModal,
		Tooltip
	},
	props: {
		form: {
			type: Object,
			default: () => {}
		}
	},
	validations: {
		form: {
			name: {
				required,
				maxLength: maxLength(20)
			},
			formRevision: {
				maxLength: maxLength(100)
			},
			description: {
				required
			}
		}
	},
	data() {
		return {
			typeOptions: [
				{ text: 'Law', value: 'L' },
				{ text: 'WKFS', value: 'W' },
				{ text: 'Iowa ADA', value: 'I' },
				{ text: 'MN ADA', value: 'M' },
				{ text: 'OR ADA', value: 'OR' },
				{ text: 'CO ADA', value: 'CO' },
				{ text: 'ID ADA', value: 'ID' }
			],
			usePDFFont: false,
			showFormCharges: false,
			autoSave: false,
			updateSupportValues: true,
			showFileModal: false,
			dateRange: {
				startDate: new Date(),
				endDate: new Date()
			},
			openPicker: 'left',
			tooltipAutosaveVisible: false
		};
	},
	mounted() {
		if (this.editionMode) {
			const { name } = CookiesConfig.globalFormAutoSave;
			this.autoSave = this.$cookies.get(name) === 'true';
		}
	},
	computed: {
		changeRemoveTitleButton() {
			if (this.form.active) {
				return 'PDF cannot be deleted while the form is in an Active status.';
			} else {
				return 'Remove file';
			}
		},
		showDeleteFileOption() {
			return !this.form.active;
		},
		hasFormValue() {
			return !isEmpty(this.form);
		},
		enabledPriceCost() {
			const allowedTypes = ['L', 'OR', 'ID', 'CO'];
			return !allowedTypes.includes(this.form.formType);
		},
		formNameCustomErrorMessage() {
			if (this.$v.form.name.$dirty) {
				if (!this.$v.form.name.required) {
					return 'Form Name is required';
				} else if (!this.$v.form.name.maxLength) {
					return `Form Name must have less than ${this.$v.form.name.$params.maxLength.max} letters`;
				} else {
					return '';
				}
			} else {
				return '';
			}
		},
		formRevisionCustomErrorMessage() {
			if (this.$v.form.formRevision.$dirty) {
				if (!this.$v.form.formRevision.maxLength) {
					return `Form Revision must have less than ${this.$v.form.formRevision.$params.maxLength.max} letters`;
				} else {
					return '';
				}
			} else {
				return '';
			}
		},
		formDescriptionCustomErrorMessage() {
			if (this.$v.form.description.$dirty) {
				if (!this.$v.form.description.required) {
					return 'Form Description is required';
				} else {
					return '';
				}
			} else {
				return '';
			}
		}
	},
	methods: {
		changeAutoSave(value) {
			this.autoSave = value;
			const { name, expiredTime } = CookiesConfig.globalFormAutoSave;
			this.$cookies.set(name, value, expiredTime);
		},
		changeUsePDFFont(val, isChecked) {
			this.usePDFFont = isChecked;
			this.form.usePDFFont = isChecked;
		},
		changeCharges(val, isChecked) {
			this.showFormCharges = isChecked;
			const hasCharges = this.hasValueFormCharges(this.form);
			if (hasCharges) {
				this.$store.dispatch('forms/resetValue', !isChecked);
			}
		},
		hasValueFormCharges(val) {
			return !!val.formType;
		},
		addReplaceFile() {
			this.showFileModal = true;
		},
		changedFile(val) {
			Object.assign(this.form, {
				laserFormFileName: val.laserFormFileName,
				pdfData: val.pdfData,
				minutesAgo: val.minutesAgo
			});
		},
		deleteFile() {
			if (!this.form.active) {
				Object.assign(this.form, {
					laserFormFileName: null,
					pdfData: null,
					minutesAgo: 0
				});
				this.$store.commit('form/SetDeletePdfForm', true);
			}
		},
		setName() {
			const hasError =
				!this.$v.form.name.required || !this.$v.form.name.maxLength;
			this.$store.commit('form/SetHasError', hasError);
		},
		setFormRevision() {
			this.$store.commit(
				'form/SetHasError',
				!this.$v.form.formRevision.maxLength
			);
		},
		setDescription() {
			const hasError = !this.$v.form.description.required;
			this.$store.commit('form/SetHasError', hasError);
		}
	},
	watch: {
		form: {
			immediate: true,
			deep: true,
			handler() {
				if (this.updateSupportValues) {
					this.showFormCharges = this.hasValueFormCharges(this.form);
					this.updateSupportValues = false;
				}
				const allowedTypes = ['L', 'OR', 'ID', 'CO'];
				if (!allowedTypes.includes(this.form.formType)) {
					this.form.formPrice = null;
					this.form.formCost = null;
				}
				this.usePDFFont = !!this.form.usePDFFont;
			}
		}
	}
};
</script>

<style lang="scss" scoped>
/**
  * Laser Forms designer
  * Can use SCSS variables = TRUE
**/

::v-deep .daterangepicker.opensleft {
	left: auto;
	right: -60px;

	@include mq($from: sm-l) {
		right: -329px;
	}
	@include mq($from: md) {
		right: -470px;
	}
	@include mq($from: md-l) {
		right: 0;
	}

	&::before {
		left: auto;
		right: 240px;

		@include mq($from: sm-l) {
			right: 511px;
		}
		@include mq($from: md) {
			right: 652px;
		}
		@include mq($from: md-l) {
			right: 182px;
		}
	}
	&::after {
		left: auto;
		right: 241px;

		@include mq($from: sm-l) {
			right: 512px;
		}
		@include mq($from: md) {
			right: 653px;
		}
		@include mq($from: md-l) {
			right: 183px;
		}
	}
}

.laser-form {
	&__content {
		background: $white;
		padding: $padding-md;
		flex-direction: column;
		flex-wrap: wrap;
		display: flex;

		@include mq($until: sm-l) {
			border-top: 1px solid $neutral-3;
		}

		@include mq($from: sm-l) {
			border: 1px solid $neutral-3;
			border-radius: $form-border-radius;
		}
	}

	&__header-horizontal-line {
		@include create-horizontal-line(16px, 1px, $gutter-xl, $neutral-3);
	}

	&__header {
		@include mq($from: md) {
			display: flex;
			align-items: center;
		}

		&-title {
			color: $black;
			width: calc(100% - 180px);
			@include ellipsis;
			font-size: $default-font-size;
			font-weight: $medium-font-weight;

			@include mq($from: sm-l) {
				display: flex;
				align-items: center;
			}
		}
		&-primary {
			display: flex;
			justify-content: space-between;

			@include mq($from: md) {
				display: flex;
				justify-content: space-between;
				flex-grow: 1;
			}
		}

		&-secondary {
			display: flex;
			align-items: center;
			justify-content: flex-start;

			@include mq($until: md) {
				margin-top: $gutter-md;
			}

			.btn {
				font-size: 24px;
			}
		}

		&-spacer {
			margin: 0 $gutter-xl;
			height: 24px;
			width: 2px;
			background: $neutral-3;

			@include mq($until: md) {
				display: none;
			}
		}
	}

	&__header-secondary {
		position: relative;
		justify-content: space-between;

		&-toggle + &-tooltip {
			display: none;
		}

		&-toggle:hover + &-tooltip {
			display: inline;
		}
	}

	::v-deep .btn--icon-only-circle {
		min-width: auto;
	}

	&__group {
		display: grid;
		gap: $gutter-xl;
		margin-bottom: $gutter-xl;

		@include mq($from: md) {
			grid-template-columns: repeat(2, 1fr);
		}

		@include mq($from: md-l) {
			grid-template-columns: repeat(3, 1fr);
		}

		&-tertiary {
			.laser-form-group__item {
				&:not(:last-child) {
					margin-bottom: $gutter-xl;
				}
			}

			@include mq($from: md) {
				display: inline-flex;
				flex-grow: 1;
				gap: $gutter-xl;

				.laser-form-group__item {
					flex-grow: 4;
				}
			}

			@include mq($from: md) {
				grid-column: 1/2;
				grid-row: 2/3;
			}

			@include mq($from: md-l) {
				grid-column: 3/4;
				grid-row: 1/2;
			}
		}
	}

	&__check {
		margin-bottom: $gutter-xl;
	}

	&__optional {
		display: flex;
		flex-wrap: wrap;
		flex-direction: column;
		margin-bottom: $gutter-md;

		&-fields-container {
			display: grid;
			grid-template-rows: repeat(3, 1fr);
			gap: $gutter-xl;
			margin-bottom: $gutter-lg;

			@include mq($from: sm-l) {
				grid-template-rows: repeat(2, 1fr);
				grid-template-columns: repeat(2, 1fr);
			}

			@include mq($from: md) {
				max-width: 740px;
				grid-template-rows: 1fr;
				grid-template-columns: repeat(3, 1fr);
			}

			div.laser-form__group-item {
				margin-bottom: 0;
			}
		}

		.laser-form__optional-horizontal-line {
			@include create-horizontal-line(16px, 1px, $gutter-xl, $neutral-3);
			margin-top: 0;
		}
	}

	&__details {
		display: flex;
		flex-wrap: wrap;
		flex-direction: column;
	}
}

.laser-form__group-item {
	&:not(:last-child) {
		margin-bottom: $gutter-xl;
	}

	::v-deep .textarea__input {
		height: 108px;
	}
}

.uploaded-file {
	&__already-uploaded {
		display: flex;
		align-items: center;
	}

	&__title {
		display: inline-block;
		font-size: 13px;
		font-weight: $default-font-weight;
		color: inherit;
		margin-bottom: $gutter-xs;
	}

	&__name {
		display: inline-block;
		max-width: 240px;
		margin-left: $gutter-sm;
		@include ellipsis;

		@include mq($from: sm-l) {
			max-width: 324px;
		}

		@include mq($from: md-l) {
			max-width: 240px;
		}

		@include mq($from: lg) {
			max-width: 300px;
		}
	}

	&__details {
		display: flex;
		justify-content: flex-end;
		font-size: $default-font-size - 4;
		color: $neutral-2;

		&-item {
			&:first-child {
				margin-right: $gutter-sm;
			}
		}
	}

	&__delete {
		display: inline-flex;
		align-self: center;
		font-size: $icon-font-size;
		margin-left: $gutter-md;
	}

	&__replace-button {
		align-self: center;
	}
}

.laser-form__footer {
	padding: $padding-md 0;
	text-align: right;

	&-container {
		margin-left: $gutter-md; //matches header
		margin-right: $gutter-md; //matches header

		@include mq($from: sm-l) {
			display: flex;
			flex-wrap: wrap;
			justify-content: flex-end;
			align-items: center;
		}

		@include mq($from: lg) {
			@include contain(1200px);
		}
	}

	&-actions {
		display: inline-flex;
		align-items: initial;
	}
}

.laser-form__footer-message {
	font-size: $default-font-size - 2;
	color: $neutral-2;

	@include mq($from: sm-l) {
		margin-right: $gutter-xxl;
	}

	&-item {
		@include mq($until: sm-l) {
			display: flex;
			justify-content: flex-end;
			margin-bottom: $gutter-md;
		}
	}
}
</style>
